import { z } from 'zod'

import { transcriptRecordSchema } from './transcriptRecord.ts'

// 指示の応答（runtime の run の SSE）と、途中からの配信（storage の streamTurn の SSE）の1イベント。
// BE は SSE を組み立て直さずに中継し、検証しないので、画面が受け取るときに検証する（ADR-001、ADR-105）。
// イベント名を event に、JSON として読んだ data を data に入れて検証する。
// BE が検証するターンの要求と応答（gatewayTurns.ts）と分け、画面だけが検証するイベントの形をここに置く

// run の SSE の notification の data。Codex の通知そのもので、params の形は method ごとに Codex が決める
const codexNotificationSchema = z.looseObject({
  method: z.string(),
  params: z.unknown(),
})

// run の SSE の approval_request の data。server、tool、arguments は type が mcpToolCall のときだけ値を持つ
const approvalRequestDataSchema = z.looseObject({
  turnId: z.string(),
  approvalId: z.string(),
  type: z.enum(['commandExecution', 'fileChange', 'mcpToolCall']),
  itemId: z.string(),
  command: z.string().nullable(),
  cwd: z.string().nullable(),
  changes: z.array(z.object({ path: z.string(), kind: z.string() })).nullable(),
  server: z.string().nullable(),
  tool: z.string().nullable(),
  arguments: z.unknown(),
  reason: z.string().nullable(),
})

// run の SSE の title の data。セッションの最初のターンで、タイトルを考えられたときだけ1回届く
const titleDataSchema = z.looseObject({
  turnId: z.string(),
  agentSessionId: z.string(),
  title: z.string(),
})

// ターンが考え始めるまでの段階。送る順に並べる。
// 段階の一覧、送る条件、画面の文言の正本は salescore-agent-docs の arch/api/progress.md にある
const turnProgressStageSchema = z.enum([
  'accepted',
  'preparing',
  'startingAgent',
  'connectingTools',
  'restoringHistory',
  'thinking',
])

export type TurnProgressStage = z.infer<typeof turnProgressStageSchema>

// run の SSE の progress の data。Codex を起動するターンでは accepted から順に、使い回すターンでは thinking だけが届く。
// 各段階は1ターンに1回だけで、前に戻らない。at は runtime が段階に入った時刻（ISO 8601）
const runProgressDataSchema = z.looseObject({
  turnId: z.string(),
  stage: turnProgressStageSchema,
  at: z.string(),
})

// run の SSE の completed の data。ターンの結果に、要求の turnId を添えたもの
const runCompletedDataSchema = z.looseObject({
  turnId: z.string(),
  status: z.enum(['completed', 'interrupted', 'failed']),
  threadId: z.string(),
  // Codex が振ったターンの ID。turnId とは別の値
  codexTurnId: z.string(),
  isNewThread: z.boolean(),
  // 利用者への最終回答。回答がなければ null
  finalMessage: z.string().nullable(),
  error: z.string().nullable(),
})

// run の SSE の error の data。ターンを最後まで実行できなかったときに、completed の代わりに1回だけ届く
const runErrorDataSchema = z.looseObject({
  turnId: z.string(),
  message: z.string(),
})

// POST /api/agent/gateway/sessions/{agentSessionId}/turns の応答（SSE）の1イベント。runtime の SSE をそのまま中継する。
// data は runtime が項目を足しても受け付けるよう、知らない項目を残す。completed か error は最後に1回届く
export const runTurnSseEventSchema = z.discriminatedUnion('event', [
  z.object({ event: z.literal('notification'), data: codexNotificationSchema }),
  z.object({ event: z.literal('progress'), data: runProgressDataSchema }),
  z.object({ event: z.literal('approval_request'), data: approvalRequestDataSchema }),
  z.object({ event: z.literal('title'), data: titleDataSchema }),
  z.object({ event: z.literal('completed'), data: runCompletedDataSchema }),
  z.object({ event: z.literal('error'), data: runErrorDataSchema }),
])

export type RunTurnSseEvent = z.infer<typeof runTurnSseEventSchema>

// 配信を始めた後の失敗の data。storage の POST / の失敗の応答と同じ形
const streamFailureDataSchema = z.object({
  success: z.literal(false),
  error: z.object({ code: z.string(), message: z.string() }),
})

// GET /api/agent/gateway/sessions/{agentSessionId}/turns/{turnId}/stream の応答（SSE）の1イベント。storage の streamTurn の SSE をそのまま中継する。
// records で追記されたレコードを送り、ターンが終われば completed、配信の上限を過ぎれば reconnect、失敗すれば error で終わる
export const streamTurnSseEventSchema = z.discriminatedUnion('event', [
  z.object({
    event: z.literal('records'),
    data: z.object({ records: z.array(transcriptRecordSchema), nextOffset: z.number() }),
  }),
  // status は終了イベントの status を文字列にした値。getTurns の status と違い、巻き戻しと知らない値を読み替えない
  z.object({ event: z.literal('completed'), data: z.object({ status: z.string(), nextOffset: z.number() }) }),
  // 画面は nextOffset からつなぎ直す
  z.object({ event: z.literal('reconnect'), data: z.object({ nextOffset: z.number() }) }),
  z.object({ event: z.literal('error'), data: streamFailureDataSchema }),
])

export type StreamTurnSseEvent = z.infer<typeof streamTurnSseEventSchema>
